import React from 'react';
import { ArrowDownToLineIcon, ArrowUpRightFromSquareIcon } from '@revfluence/fresh-icons/regular/esm';
import { cn } from '@/shadcn/lib/utils';

export interface CardActionButtonsProps {
  /** Additional CSS classes */
  className?: string;
  /** Show download button */
  showDownload?: boolean;
  /** Show external link button */
  showExternal?: boolean;
  /** Download button click handler */
  onDownload?: () => void;
  /** External link button click handler */
  onOpenExternal?: () => void;
  /** Custom action buttons to render */
  children?: React.ReactNode;
}

/**
 * CardActionButtons
 * -----------------
 * Renders action buttons in the top-right corner of a card overlay.
 * Supports download, external link, and custom action buttons.
 *
 * Features:
 * - Keyboard navigation (Enter/Space)
 * - Accessibility labels and ARIA attributes
 * - Click event propagation prevention
 * - Consistent styling with hover/focus states
 * - Error handling for action callbacks
 */
export const CardActionButtons = React.forwardRef<HTMLDivElement, CardActionButtonsProps>(
  ({
    className,
    showDownload = false,
    showExternal = true,
    onDownload,
    onOpenExternal,
    children,
    ...props
  }, ref) => {
    const handleDownloadClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onDownload) {
        try {
          onDownload();
        } catch (error) {
          console.error('Error in download handler:', error);
        }
      }
    };

    const handleExternalClick = (e: React.MouseEvent) => {
      e.stopPropagation();
      if (onOpenExternal) {
        try {
          onOpenExternal();
        } catch (error) {
          console.error('Error in external link handler:', error);
        }
      }
    };

    const handleKeyDown = (e: React.KeyboardEvent, action: 'download' | 'external') => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        e.stopPropagation();

        if (action === 'download' && onDownload) {
          try {
            onDownload();
          } catch (error) {
            console.error('Error in download handler:', error);
          }
        } else if (action === 'external' && onOpenExternal) {
          try {
            onOpenExternal();
          } catch (error) {
            console.error('Error in external link handler:', error);
          }
        }
      }
    };

    return (
      <div
        ref={ref}
        className={cn('absolute top-3 right-3 flex gap-2', className)}
        {...props}
      >
        {showDownload && onDownload && (
          <div
            className="rounded-full p-1 cursor-pointer hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20"
            onClick={handleDownloadClick}
            onKeyDown={(e) => handleKeyDown(e, 'download')}
            tabIndex={0}
            role="button"
            aria-label="Download content"
            title="Download content"
          >
            <ArrowDownToLineIcon className="h-4 w-4 text-white drop-shadow-lg" />
          </div>
        )}
        {showExternal && onOpenExternal && (
          <div
            className="rounded-full p-1 cursor-pointer hover:bg-white/10 transition-colors focus:outline-none focus:ring-2 focus:ring-white/20"
            onClick={handleExternalClick}
            onKeyDown={(e) => handleKeyDown(e, 'external')}
            tabIndex={0}
            role="button"
            aria-label="Open content in new window"
            title="Open in new window"
          >
            <ArrowUpRightFromSquareIcon className="h-4 w-4 text-white drop-shadow-lg" />
          </div>
        )}
        {children}
      </div>
    );
  }
);

CardActionButtons.displayName = 'CardActionButtons';
